import React, { useCallback } from 'react';
import { ReactComponent as FileDownloadIcon } from 'assets/icons/file-download.svg';
import { GetFhirAppointmentDiagnosticReportOutput } from 'graphql/generated/hasura';
import { useGetFileFromStorage } from 'hooks/useGetFileFromStorage';
import { useGetComponent } from 'hooks/useGetComponent';
import { componentIds } from 'utilities/constants';
import ButtonComponent from 'components/button/buttonComponent';
import { Card } from '../Card';
import { useFieldArray, useFormContext } from 'react-hook-form';
import ControlledCheckbox from 'components/stepper/field-components/FormCheckboxGroup';
import { formatDateForAppointment } from '../../../../utilities/functions';
import moment from 'moment';

interface TestKitsProps {
  patientId?: string;
}

const TestKitSummary: React.FC<TestKitsProps> = ({ patientId }) => {
  const { fetchFileFromStorage } = useGetFileFromStorage();
  const { control } = useFormContext();

  const { fields } = useFieldArray({
    control,
    name: 'diagnosticReports',
  });

  const { data: locale } = useGetComponent({
    componentId: componentIds.TEST_KIT_LABELS,
    locale: 'en',
  });

  const getDownloadFunction = useCallback(
    async (diagnosticReportId: string) => {
      if (!diagnosticReportId) return;
      const pdfLink = await fetchFileFromStorage(diagnosticReportId, patientId);
      window.open(pdfLink, '_blank');
    },
    [fetchFileFromStorage, patientId],
  );

  if (!fields.length) {
    return (
      <Card
        title={'Diagnostic test kit results'}
        className="flex-col items-start gap-6 -mb-5"
        subtitle={'Choose the test kit results discussed with the patient'}
        subtitleClassName="!text-base !font-medium"
        titleClassName="flex text-h4 -mb-4 text-btn-black font-semibold"
        backgroundColor="bg-white desktop:!gap-30"
        showSeparator
      >
        <div className="w-full py-30 text-center">
          <p className="text-med-gray-3 font-medium">
            No pending test kits available for this patient
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={'Diagnostic test kit results'}
      className="flex-col items-start gap-6 -mb-5"
      subtitle={'Choose the test kit results discussed with the patient'}
      subtitleClassName="!text-base !font-medium"
      titleClassName="flex text-h4 -mb-4 text-btn-black font-semibold"
      backgroundColor="bg-white desktop:!gap-30"
      defaultExpanded
      showSeparator
    >
      <div className="w-full max-h-[300px] overflow-y-auto flex flex-col gap-2.5">
        {fields.map((field, index) => {
          const {
            diagnosticReportTestType,
            providerPdfId,
            patientPdf,
            providerPdf,
            createdAt,
          } = field as unknown as GetFhirAppointmentDiagnosticReportOutput;

          const creation =
            patientPdf?.creation || providerPdf?.creation || createdAt;

          return (
            <div
              key={field.id}
              className="flex flex-col desktop:flex-row justify-between border border-gray-opacity-15 rounded-lg desktop:items-center w-full gap-2.5 py-2.5 px-2.5 pl-5"
            >
              <div className="flex items-center gap-2">
                <ControlledCheckbox
                  label={''}
                  name={`diagnosticReports.${index}.isCheckbox`}
                />
                <div className="flex flex-col gap-2.5">
                  <div className="flex flex-col text-btn-black font-semibold">
                    {locale?.[diagnosticReportTestType] ??
                      diagnosticReportTestType}
                  </div>
                  <p className="text-sm text-med-gray-3 font-medium">
                    Date & Time:{' '}
                    {formatDateForAppointment(
                      moment(creation || undefined).toDate(),
                      {
                        simplified: true,
                        withTimeZone: false,
                      },
                    )}
                  </p>
                </div>
              </div>

              <ButtonComponent
                onClick={() => getDownloadFunction(providerPdfId as string)}
                type={'outlined'}
                testID={'download-file-button'}
                htmlButtonType="button"
                Icon={FileDownloadIcon}
                iconHeight="w-[15px] h-[18px] cursor-pointer fill-current"
                iconPosition="left"
                disabled={!providerPdfId}
                className="flex !border items-center gap-2.5 !text-xs !w-auto"
              >
                <span>Results</span>
              </ButtonComponent>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default TestKitSummary;
